"use client";

import { focusRing } from "@decibeltrade/web-core/recipes";
import { useLocale } from "next-intl";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import { css } from "styled-system/css";
import { token } from "styled-system/tokens";

import type { WeeklyEarningPoint } from "@/contexts/queries/rewards/use-rewards-summary";
import { chartTooltipStyles } from "@/recipes/chart";
import { NumberFormatter } from "@/utils/number-formatter";

const END_DOT_RADIUS_PX = 2.5;
const ACTIVE_DOT_RADIUS_PX = 3;
const STROKE_WIDTH_PX = 1.5;
const TOOLTIP_OFFSET_Y_PX = 12;

const DISABLED_LINE_COLOR = token("colors.light.500");
const DISABLED_FILL_COLOR = token("colors.dark.100");
const ACTIVE_COLOR = token("colors.yellow.500");
const TINT_COLOR = token("colors.green.100");

const PLACEHOLDER_BREAKDOWN: WeeklyEarningPoint[] = [
  { weekStartTsSec: 0, amountUsd: 28 },
  { weekStartTsSec: 1, amountUsd: 32 },
  { weekStartTsSec: 2, amountUsd: 30 },
  { weekStartTsSec: 3, amountUsd: 42 },
  { weekStartTsSec: 4, amountUsd: 36 },
  { weekStartTsSec: 5, amountUsd: 28 },
  { weekStartTsSec: 6, amountUsd: 22 },
  { weekStartTsSec: 7, amountUsd: 38 },
  { weekStartTsSec: 8, amountUsd: 60 },
  { weekStartTsSec: 9, amountUsd: 52 },
  { weekStartTsSec: 10, amountUsd: 95 },
  { weekStartTsSec: 11, amountUsd: 120 },
];

interface WeeklyTrendChartProps {
  data: WeeklyEarningPoint[] | undefined;
}

export function WeeklyTrendChart({ data }: WeeklyTrendChartProps) {
  const disabled = !data || data.length === 0;
  const points = disabled ? PLACEHOLDER_BREAKDOWN : data;
  const lineColor = disabled ? DISABLED_LINE_COLOR : ACTIVE_COLOR;
  const baseColor = disabled ? DISABLED_FILL_COLOR : ACTIVE_COLOR;
  const baseId = disabled ? "rewards-trend-area-base-disabled" : "rewards-trend-area-base";
  const lastIndex = points.length - 1;
  const [wrapperEl, setWrapperEl] = useState<HTMLDivElement | null>(null);
  const locale = useLocale();

  const fmtWeek = useMemo(() => {
    const dateFmt = new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return (tsSec: number) => dateFmt.format(new Date(tsSec * 1000));
  }, [locale]);

  return (
    <div ref={setWrapperEl} className={wrapperStyles}>
      <ResponsiveContainer width="100%" height="100%" className={focusRing()}>
        <AreaChart data={points} margin={{ top: 6, right: 8, bottom: 6, left: 8 }}>
          <defs>
            <linearGradient id={baseId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={baseColor} stopOpacity={1} />
              <stop offset="100%" stopColor={baseColor} stopOpacity={0} />
            </linearGradient>
            {!disabled && (
              <linearGradient id="rewards-trend-area-tint" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={TINT_COLOR} stopOpacity={0.5} />
                <stop offset="100%" stopColor={TINT_COLOR} stopOpacity={0} />
              </linearGradient>
            )}
          </defs>
          {!disabled && (
            <Tooltip
              cursor={false}
              content={(props: TrendTooltipContentProps) => (
                <PortaledTrendTooltip
                  {...props}
                  wrapperEl={wrapperEl}
                  fmtWeek={fmtWeek}
                  locale={locale}
                />
              )}
            />
          )}
          <Area
            type="linear"
            dataKey="amountUsd"
            stroke="none"
            fill={`url(#${baseId})`}
            isAnimationActive={false}
            dot={false}
          />
          <Area
            type="linear"
            dataKey="amountUsd"
            stroke={lineColor}
            strokeWidth={STROKE_WIDTH_PX}
            fill={disabled ? "none" : "url(#rewards-trend-area-tint)"}
            isAnimationActive={false}
            dot={(props) => <EndDot {...props} fill={lineColor} lastIndex={lastIndex} />}
            activeDot={
              disabled ? false : { r: ACTIVE_DOT_RADIUS_PX, fill: lineColor, strokeWidth: 0 }
            }
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

const wrapperStyles = css({ w: "full", h: "full" });

interface TooltipPayload {
  payload: WeeklyEarningPoint;
}

interface TrendTooltipContentProps {
  active?: boolean;
  payload?: TooltipPayload[];
  coordinate?: { x?: number; y?: number };
}

interface PortaledTrendTooltipProps extends TrendTooltipContentProps {
  wrapperEl: HTMLDivElement | null;
  fmtWeek: (tsSec: number) => string;
  locale: string;
}

function PortaledTrendTooltip({
  active,
  payload,
  coordinate,
  wrapperEl,
  fmtWeek,
  locale,
}: PortaledTrendTooltipProps) {
  if (typeof document === "undefined") return null;
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  const rect = wrapperEl?.getBoundingClientRect();
  if (!rect) return null;
  const cx = coordinate?.x ?? 0;
  const cy = coordinate?.y ?? 0;
  const left = rect.left + cx;
  const top = rect.top + cy - TOOLTIP_OFFSET_Y_PX;

  return createPortal(
    <div
      className={portalWrapperStyles}
      style={{ left: `${left}px`, top: `${top}px` }}
      role="tooltip"
    >
      <div className={chartTooltipStyles}>
        <span className={css({ textStyle: "highlight.sm", color: "text.tertiary" })}>
          {fmtWeek(point.weekStartTsSec)}
        </span>
        <span className={css({ textStyle: "highlight.md", color: "text.primary" })}>
          {NumberFormatter.compactHeading(point.amountUsd, { currency: true, locale })}
        </span>
      </div>
    </div>,
    document.body,
  );
}

const portalWrapperStyles = css({
  position: "fixed",
  transform: "translate(-50%, -100%)",
  pointerEvents: "none",
  zIndex: "popover",
});

interface EndDotProps {
  cx?: number;
  cy?: number;
  index?: number;
  fill: string;
  lastIndex: number;
}

function EndDot({ cx, cy, index, fill, lastIndex }: EndDotProps) {
  if (cx == null || cy == null || index !== lastIndex) {
    return null;
  }
  return <circle cx={cx} cy={cy} r={END_DOT_RADIUS_PX} fill={fill} />;
}
